/**
 * Sign-in mutations — #mc-phase2.
 *
 * Five-step ceremony for the operator:
 *   1. useRequestOtp({ email })                      → emails a code
 *   2. useVerifyOtp({ email, code })                 → enrollment cookie + hasCredentials
 *   3a. (no credentials) useStartRegistration() →
 *       browser.startRegistration(opts) →
 *       useFinishRegistration({ response, deviceLabel? })
 *   3b. (has credentials) useStartAuthentication({ email }) →
 *       browser.startAuthentication(opts) →
 *       useFinishAuthentication({ email, response })
 *
 * On success of the terminal step (finish registration or finish
 * authentication), we invalidate ["me"] so the Shell's `useMe` query
 * re-runs and the chrome re-renders authenticated.
 *
 * The WebAuthn browser-side ceremony lives in this file via the
 * @simplewebauthn/browser package; the wrappers below call its
 * `startRegistration` / `startAuthentication` functions with the
 * server-issued options and pass the result back to the server.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "./api";

// ---------------------------------------------------------------------------
// Lazy-loaded WebAuthn browser SDK. The library is small but interactive-
// only; loading it lazily keeps the initial bundle leaner. Tests stub
// `__setWebAuthnBrowser` to inject fake ceremony fns.
// ---------------------------------------------------------------------------

type WebAuthnBrowser = {
  startRegistration: (opts: { optionsJSON: unknown }) => Promise<unknown>;
  startAuthentication: (opts: { optionsJSON: unknown }) => Promise<unknown>;
};

let injectedBrowser: WebAuthnBrowser | null = null;

export function __setWebAuthnBrowser(b: WebAuthnBrowser | null): void {
  injectedBrowser = b;
}

async function loadWebAuthnBrowser(): Promise<WebAuthnBrowser> {
  if (injectedBrowser) return injectedBrowser;
  // Static-literal dynamic import — Vite resolves this at build time and
  // code-splits @simplewebauthn/browser into a chunk fetched only when a
  // ceremony actually runs. A *variable*-spec import (`/* @vite-ignore */`
  // + a string variable) is left as a bare runtime `import()` that the
  // browser can't resolve from a static bundle — do not reintroduce it.
  const mod = (await import("@simplewebauthn/browser")) as WebAuthnBrowser;
  return mod;
}

// ---------------------------------------------------------------------------
// Mutation hooks.
// ---------------------------------------------------------------------------

/**
 * ADR-0093 — /request response carries `nextStep` so the SPA routes
 * to the right pane (email-code | passkey | sms) based on the
 * operator's committed strategy. First-time and email-only operators
 * see `email-code`; subsequent passkey/sms users skip the OTP.
 */
export type RequestOtpResponse = {
  ok: true;
  nextStep: "email-code" | "sms" | "passkey";
};

export function useRequestOtp() {
  return useMutation<RequestOtpResponse, Error, { email: string }>({
    mutationFn: async ({ email }) => {
      return apiPost<RequestOtpResponse>("/api/auth/request", { email });
    },
  });
}

/**
 * ADR-0093 — /verify response carries `promptFactorPicker`. Combined
 * with `hasCredentials` it drives the SPA's post-verify branch:
 *
 *   hasCredentials=true, promptFactorPicker=false → assert
 *   hasCredentials=true, promptFactorPicker=true  → assert + self-heal commit
 *   hasCredentials=false, promptFactorPicker=true → chooser (first sign-in)
 *   hasCredentials=false, promptFactorPicker=false → passkey register (defensive)
 */
export type VerifyOtpResponse = {
  ok: true;
  hasCredentials: boolean;
  promptFactorPicker: boolean;
};

export function useVerifyOtp() {
  return useMutation<
    VerifyOtpResponse,
    Error,
    { email: string; code: string }
  >({
    mutationFn: async ({ email, code }) => {
      return apiPost<VerifyOtpResponse>("/api/auth/verify", { email, code });
    },
  });
}

/**
 * Register a new passkey: server issues options → browser ceremony →
 * server verifies attestation. Combined into one mutation because the
 * two halves only make sense as a pair from the UI's perspective.
 */
export function useEnrollPasskey() {
  const queryClient = useQueryClient();
  return useMutation<{ email: string }, Error, { deviceLabel?: string }>({
    mutationFn: async ({ deviceLabel }) => {
      const optionsJSON = await apiPost<unknown>(
        "/api/auth/passkey/register/begin",
        {},
      );
      const browser = await loadWebAuthnBrowser();
      const response = await browser.startRegistration({ optionsJSON });
      const body: { response: unknown; deviceLabel?: string } = { response };
      if (deviceLabel) body.deviceLabel = deviceLabel;
      return apiPost<{ email: string }>(
        "/api/auth/passkey/register/finish",
        body,
      );
    },
    onSuccess: async () => {
      // Refresh useMe so the chrome rehydrates with the new session.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/**
 * Sign in with an existing passkey: server issues challenge → browser
 * assertion → server verifies. Same packaging rationale as
 * `useEnrollPasskey`.
 */
export function useSignInWithPasskey() {
  const queryClient = useQueryClient();
  return useMutation<{ email: string }, Error, { email: string }>({
    mutationFn: async ({ email }) => {
      const optionsJSON = await apiPost<unknown>("/api/auth/passkey/begin", {
        email,
      });
      const browser = await loadWebAuthnBrowser();
      const response = await browser.startAuthentication({ optionsJSON });
      return apiPost<{ email: string }>("/api/auth/passkey/finish", {
        email,
        response,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ---------------------------------------------------------------------------
// ADR-0093 — SMS peer factor + first-sign-in chooser.
// ---------------------------------------------------------------------------

/**
 * Begin SMS phone-of-record enrollment. Server normalises the input
 * to E.164, sends a Twilio Verify code, and stashes the phone in
 * KV `sms-register:<email>` (5-min TTL).
 */
export function useRegisterPhoneBegin() {
  return useMutation<{ ok: true }, Error, { phone: string }>({
    mutationFn: async ({ phone }) => {
      return apiPost<{ ok: true }>("/api/auth/phone/register-begin", {
        phone,
      });
    },
  });
}

/**
 * Finish SMS phone-of-record enrollment. Server verifies the code
 * against Twilio and persists `phone_e164` + `phone_verified_at`.
 * Does NOT commit the strategy — see `useCommitAuthStrategy`.
 */
export function useRegisterPhoneFinish() {
  return useMutation<{ ok: true }, Error, { code: string }>({
    mutationFn: async ({ code }) => {
      return apiPost<{ ok: true }>("/api/auth/phone/register-finish", {
        code,
      });
    },
  });
}

/**
 * Commit a sticky strategy for the operator. Mints a 7-day session
 * cookie on success (server-side); chooser flow ends here. Refresh
 * useMe so the chrome rehydrates with the new session.
 */
export function useCommitAuthStrategy() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: true },
    Error,
    { strategy: "email-only" | "sms" | "passkey" }
  >({
    mutationFn: async ({ strategy }) => {
      return apiPost<{ ok: true }>("/api/auth/strategy", { strategy });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/**
 * Subsequent SMS sign-in. Caller has already hit /api/auth/request,
 * which sent a Twilio code; this verifies it and mints the session.
 */
export function useVerifySmsSignIn() {
  const queryClient = useQueryClient();
  return useMutation<
    { email: string },
    Error,
    { email: string; code: string }
  >({
    mutationFn: async ({ email, code }) => {
      return apiPost<{ email: string }>("/api/auth/sms/verify", {
        email,
        code,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
